import React, {useCallback, useImperativeHandle, useState} from 'react';
import clsx from 'clsx';
import {nanoid} from 'nanoid';
import {type IconDefinition, faEllipsisV, faQuestion} from '@netx/core-icons';
import {
	type MenuProps as MuiMenuProps,
	type PopoverProps,
	Skeleton,
	type SkeletonProps,
} from '@mui/material';
import {
	ActionMenuContainer,
	type ActionMenuContainerProps,
} from '@netx/core-components/ActionMenu';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useMediaQuery} from '@netx/core-components/hooks/useMediaQuery';
import {
	IconButton,
	type IconButtonProps,
} from '@netx/core-components/IconButton';
import {
	composeClasses,
	styled,
	useTheme,
	type WithClasses,
} from '@netx/core-components/Theme';
import {i18n} from '@netx/core-next/locales/i18n-instance';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import type {ReactAction} from '@netx/core/bootstrap';
import {
	ACTION_MENU_ICON_BUTTON_CLASS_NAME,
	actionMenuIconButtonClasses,
	type ActionMenuIconButtonClasses,
	getActionMenuIconButtonUtilityClass,
} from './actionMenuIconButtonClasses';

export type {ReactAction} from '@netx/core/bootstrap';

export type ActionMenuIconButtonOwnerState = WithClasses<
	ActionMenuIconButtonClasses,
	{
		buttonVariant: IconButtonProps['variant'];
	}
>;

const useUtilityClasses = (ownerState: ActionMenuIconButtonOwnerState) => {
	const {buttonVariant, classes} = ownerState;
	const slots = {
		root: ['root'],
		menu: ['menu'],
		skeleton: ['skeleton', buttonVariant],
	};
	return composeClasses(slots, getActionMenuIconButtonUtilityClass, classes);
};

const ActionMenuIconButtonMenuRoot = styled(ActionMenuContainer, {
	name: ACTION_MENU_ICON_BUTTON_CLASS_NAME,
	slot: 'Menu',
})(({theme}) => ({}));

const ActionMenuIconButtonSkeleton = styled(Skeleton, {
	name: ACTION_MENU_ICON_BUTTON_CLASS_NAME,
	slot: 'Skeleton',
})(({theme}) => ({
	height: 42,
	width: 42,
	[`&.${actionMenuIconButtonClasses.fab}`]: {
		height: 56,
		width: 56,
	},
}));

export type ActionMenuIconButtonAction = {
	close: () => void;
	open: () => void;
};

export interface ActionMenuIconButtonProps
	extends
		WithClasses<
			ActionMenuIconButtonClasses,
			Partial<Omit<MuiMenuProps, 'action' | 'children' | 'onClick'>>
		>,
		Pick<ActionMenuContainerProps, 'ActionMenuHeader'> {
	action?: React.RefObject<ActionMenuIconButtonAction>;
	id?: string;
	actions: Array<ReactAction | ReactAction[]>;
	buttonAvatar?: React.ReactElement;
	alwaysUseEllipsis?: boolean;
	icon?: IconDefinition;
	disabled?: boolean;
	loading?: boolean;
	title?: string;
	onAction?: (action: ReactAction) => void;
	onClick?: (event: React.MouseEvent<HTMLElement>) => void;
	IconButtonProps?: Partial<IconButtonProps>;
	SkeletonProps?: Partial<SkeletonProps>;
	'data-testid'?: string;
}

const onActionDefault = (action: ReactAction) => action?.cb();

export const ActionMenuIconButtonPrimitive = (
	{
		classes: _classes,
		'data-testid': dataTestId,
		className,
		action,
		actions,
		alwaysUseEllipsis,
		buttonAvatar,
		icon,
		disabled,
		loading,
		tabIndex,
		title,
		IconButtonProps,
		SkeletonProps,
		onAction = onActionDefault,
		onClose,
		onClick,
		...props
	}: ActionMenuIconButtonProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement>();
	const [open, setOpen] = useState(false);

	const ownerState: ActionMenuIconButtonOwnerState = {
		buttonVariant: IconButtonProps?.variant ?? 'default',
	};

	const classes = useUtilityClasses(ownerState);

	useImperativeHandle(
		action,
		() => ({
			close: () => setOpen(false),
			open: () => setOpen(true),
		}),
		[],
	);

	const handleClose = useConstCallback<Required<PopoverProps>['onClose']>(
		(event: {}, reason) => {
			setOpen(false);
			onClose?.(event, reason);
		},
	);

	const handleShowClick = useConstCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			event.preventDefault();

			onClick?.(event);

			setAnchorEl(event.currentTarget);
			setOpen(true);
		},
	);

	const handleActionClick = useConstCallback(
		(event: React.MouseEvent<HTMLElement>, action: ReactAction) => {
			event.stopPropagation();
			event.preventDefault();
			setOpen(false);
			onAction(action);
			onClose?.(event, 'escapeKeyDown');
		},
	);

	const buttonTitle = title != null ? title : i18n.t('i18n.openMenu');

	// TODO: cleanup
	if (loading)
		return (
			<ActionMenuIconButtonSkeleton
				aria-busy="true"
				className={classes.skeleton}
				data-testid={dataTestId ?? 'action-menu-toggle'}
				variant={IconButtonProps?.squaredBorder ? 'rectangular' : 'circular'}
				{...SkeletonProps}
				style={{
					borderRadius: IconButtonProps?.squaredBorder ? 4 : undefined,
					...IconButtonProps?.style,
					...SkeletonProps?.style,
				}}
				sx={{...IconButtonProps?.sx, ...SkeletonProps?.sx}}
			/>
		);

	if (!actions.length) return null;

	// Just show the single action as an icon button
	if (
		!alwaysUseEllipsis &&
		actions.length === 1 &&
		!Array.isArray(actions[0])
	) {
		const action = actions[0];
		return (
			<IconButton
				data-testid={dataTestId ?? 'action-menu-single-action'}
				disabled={disabled}
				icon={action.icon || faQuestion}
				label={action.label}
				tabIndex={tabIndex}
				onClick={(event) => handleActionClick(event, action)}
				{...IconButtonProps}
				className={clsx(classes.root, className)}
			/>
		);
	}

	const uuid = `action-menu-${nanoid()}`;

	return (
		<>
			<IconButton
				data-testid={dataTestId ?? 'action-menu-toggle'}
				disabled={disabled}
				avatar={buttonAvatar}
				icon={buttonAvatar ? undefined : icon || faEllipsisV}
				label={buttonTitle}
				tabIndex={tabIndex}
				onClick={handleShowClick}
				{...IconButtonProps}
				className={clsx(classes.root, className)}
				aria-haspopup="true"
				aria-expanded={open}
				aria-controls={open ? uuid : undefined}
			/>
			{anchorEl != null && (
				<ActionMenuIconButtonMenuRoot
					{...(props as ActionMenuContainerProps)}
					id={uuid}
					actions={actions}
					slotProps={{
						popoverProps: {
							anchorEl,
							TransitionProps: {
								...props.TransitionProps,
								onExited: splitEventHandler(
									props.TransitionProps?.onExited,
									() => {
										setAnchorEl(undefined);
									},
								),
							},
						},
					}}
					onAction={handleActionClick}
					onClose={handleClose}
					open={open}
				/>
			)}
		</>
	);
};

export const ActionMenuIconButton = React.forwardRef(
	ActionMenuIconButtonPrimitive,
) as (props: ActionMenuIconButtonProps) => JSX.Element;

export default ActionMenuIconButton;
